import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import dayjs, { type Dayjs } from "dayjs";
import type { components } from "@qorakul/shared/api";
import { api } from "./api.ts";

export type AttendanceCounts = components["schemas"]["AttendanceCounts"];
export type StudentAttendanceSummary = components["schemas"]["StudentAttendanceSummary"];
export type ClassAttendanceSummary = components["schemas"]["ClassAttendanceSummary"];
export type TeacherAttendanceSummary = components["schemas"]["TeacherAttendanceSummary"];
export type AttendanceReportSort = components["schemas"]["AttendanceReportSort"];
export type AttendancePatternResult = components["schemas"]["AttendancePatternResult"];
export type FullAttendanceClassFinding = components["schemas"]["FullAttendanceClassFinding"];
export type TeacherWithoutLateOrAbsentFinding = components["schemas"]["TeacherWithoutLateOrAbsentFinding"];
export type LateMarkedLessonFinding = components["schemas"]["LateMarkedLessonFinding"];
export type UnmarkedLessonFinding = components["schemas"]["UnmarkedLessonFinding"];

export type SummaryKind = "students" | "classes" | "teachers";

/** Longest range the API accepts (AttendanceReportRange.MaxDays), inclusive of both ends. */
export const MAX_RANGE_DAYS = 186;

export interface ReportRange {
  from: string;
  to: string;
}

export interface SummaryParams {
  page: number;
  pageSize: number;
  sort: AttendanceReportSort;
  descending: boolean;
}

const ISO_DATE = "YYYY-MM-DD";

export function toRange(from: Dayjs, to: Dayjs): ReportRange {
  return { from: from.format(ISO_DATE), to: to.format(ISO_DATE) };
}

/** True when the range covers more days than the API accepts. */
export function isRangeTooLong(range: ReportRange): boolean {
  return dayjs(range.to).diff(dayjs(range.from), "day") + 1 > MAX_RANGE_DAYS;
}

export const reportKeys = {
  all: ["reports", "attendance"] as const,
  summary: (kind: SummaryKind, range: ReportRange, params: SummaryParams) => ["reports", "attendance", kind, range, params] as const,
  patterns: (range: ReportRange) => ["reports", "attendance", "patterns", range] as const,
};

type SummaryPage<K extends SummaryKind> = K extends "students"
  ? components["schemas"]["PagedResultOfStudentAttendanceSummary"]
  : K extends "classes"
    ? components["schemas"]["PagedResultOfClassAttendanceSummary"]
    : components["schemas"]["PagedResultOfTeacherAttendanceSummary"];

/** One page of an attendance summary (GET /reports/attendance/{students|classes|teachers}), sorted on the server. */
export function useAttendanceSummary<K extends SummaryKind>(kind: K, range: ReportRange, params: SummaryParams, enabled: boolean) {
  const { t } = useTranslation();
  return useQuery({
    queryKey: reportKeys.summary(kind, range, params),
    queryFn: async () => {
      const query = { From: range.from, To: range.to, Page: params.page, PageSize: params.pageSize, Sort: params.sort, Descending: params.descending };
      const result =
        kind === "students"
          ? await api.GET("/reports/attendance/students", { params: { query } })
          : kind === "classes"
            ? await api.GET("/reports/attendance/classes", { params: { query } })
            : await api.GET("/reports/attendance/teachers", { params: { query } });
      if (result.error || !result.data) throw new Error(t("app.error"));
      // The kind picked the endpoint above, so the page is that kind's generated type.
      return result.data as SummaryPage<K>;
    },
    placeholderData: keepPreviousData,
    enabled,
  });
}

/** The unusual-pattern report (GET /reports/attendance/patterns). */
export function useAttendancePatterns(range: ReportRange, enabled: boolean) {
  const { t } = useTranslation();
  return useQuery({
    queryKey: reportKeys.patterns(range),
    queryFn: async () => {
      const { data, error } = await api.GET("/reports/attendance/patterns", { params: { query: { From: range.from, To: range.to } } });
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
    placeholderData: keepPreviousData,
    enabled,
  });
}
